import React, { useState } from 'react';
import { ChevronDown, Check, Plus, Minus, ShoppingCart, Calendar, Shield, Clock } from 'lucide-react';
import ScrollToSectionButton from '../../components/ScrollToSectionButton';
import PurchaseFormContent from '../../components/PurchaseStripeSimple';

const HeroSection = () => {
  return (
    <section 
      className="relative flex items-center bg-beige-light"
      style={{ minHeight: 'var(--content-height)' }}
    >
      <div 
        className="absolute inset-0 bg-cover bg-top bg-no-repeat md:hidden"
        style={{ 
          backgroundImage: 'url("/images/products/il-cibo-che-purifica/copertina-mobile.webp")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-beige-light/75 to-beige-light"></div>
      </div>
      
      <div className="container relative z-10 mx-auto px-4 flex flex-col md:flex-row items-start h-full">
        <div className="md:hidden flex flex-col justify-end w-full h-full" style={{ minHeight: 'calc(var(--content-height) - 0rem)' }}>
          <div className="p-4 rounded-t-3xl mb-4">
            <div className="flex items-center mb-4">
              <span className="text-2xl mr-3 text-green">🌿</span>
              <h1 className="h2 text-green">
                Il Cibo che Purifica
              </h1>
            </div>
            <p className="h4 text-text-dark mb-4">
              con la Dott.ssa Alessandra Filzoli
            </p>
            <p className="body text-text-dark mt-4 mb-6">
              Costruisci oggi le basi per un corpo più leggero, una mente più lucida e un'energia più viva.
            </p>
            <ScrollToSectionButton 
              targetId="acquista"
              className="flex h4 items-center justify-center px-6 py-5 w-full bg-green text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
            >
              Acquista ora
              <ChevronDown className="ml-2" size={20} />
            </ScrollToSectionButton>
          </div>
        </div>

        <div className="hidden md:flex md:w-1/2 md:pr-8 flex-col">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3 text-green">🌿</span>
            <h1 className="h1 text-green">
              Il Cibo che Purifica
            </h1>
          </div>
          <p className="h3 text-text-dark mb-6">
            con la Dott.ssa Alessandra Filzoli
          </p>
          <p className="body text-text-dark mt-6 mb-8">
            Costruisci oggi le basi per un corpo più leggero, una mente più lucida e un'energia più viva. 
            Un E-Book + Videoricette per depurarti in modo naturale, riconnetterti con il tuo corpo e creare abitudini consapevoli che durano nel tempo.
          </p>
          <ScrollToSectionButton 
            targetId="acquista"
            className="flex h4 items-center justify-center px-6 py-5 mt-4 bg-green text-beige-light font-bold rounded-full hover:bg-accent transition-colors duration-300 shadow-md"
          >
            Acquista ora
            <ChevronDown className="ml-2" size={20} />
          </ScrollToSectionButton>
        </div>
        <div className="hidden md:block md:w-1/2 mt-8 md:mt-0">
          <div className="rounded-15 aspect-[3/4] w-full max-w-md mx-auto overflow-hidden shadow-lg">
            <img 
              src="/images/products/il-cibo-che-purifica/copertina.webp" 
              alt="Copertina del corso Il Cibo che Purifica con la Dott.ssa Alessandra Filzoli"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const IntroSection = () => {
  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="h2 text-green mb-8">
              <span className="text-accent">✨</span> Il tuo corpo non ha bisogno di una dieta.
            </h2>
            <p className="body text-text-dark">
              Il tuo corpo ha bisogno di una rinascita. Viviamo spesso sommersi da stress, alimenti processati, 
              ritmi innaturali e abitudini che ci appesantiscono dentro e fuori. Questo E-book con videoricette che ti 
              aiuterà a ritrovare il tuo centro, depurando il corpo e favorendo un dimagrimento dolce e duraturo, 
              grazie agli insegnamenti di una vita intera di studio, pratica e medicina integrata.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutDoctorSection = () => {
  return (
    <section className="bg-beige-light py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/3">
              <div className="rounded-full overflow-hidden w-48 h-48 mx-auto shadow-md">
                <img 
                  src="/images/products/il-cibo-che-purifica/dottoressa.webp" 
                  alt="Dott.ssa Alessandra Filzoli"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:w-2/3">
              <h2 className="h3 text-green mb-4">
                <span className="text-accent">👩‍⚕️</span> Chi è la Dott.ssa Alessandra Filzoli?
              </h2>
              <p className="body text-text-dark">
                Medico agopuntore, terapeuta e appassionata ricercatrice del benessere umano, 
                unisce la saggezza della medicina cinese, la scienza occidentale e l'intelligenza del corpo. 
                Con oltre 20 anni di esperienza, ti guida in un percorso che non è solo nutrizionale, 
                ma profondamente trasformativo.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhyDetoxSection = () => {
  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="h2 text-green mb-6">
              <span className="text-accent">🌀</span> Perché depurare il corpo è il primo passo verso il benessere?
            </h2>
            <p className="body text-text-dark">
              Secondo la visione orientale, l'essere umano è un microcosmo in dialogo costante col macrocosmo. 
              Quando impariamo a scegliere il cibo giusto nel momento giusto, a ridurre il superfluo e a nutrirci 
              con presenza, l'energia vitale torna a fluire.
            </p>
          </div>
          
          <div className="bg-beige-light p-8 rounded-15 shadow-md mb-12">
            <h3 className="h3 text-green mb-6 text-center">Questo percorso ti insegnerà a:</h3>
            <ul className="space-y-4">
              {[
                "Ascoltare i segnali del tuo corpo",
                "Riconoscere le tossine (fisiche ed emotive) che ti appesantiscono",
                "Riscoprire la vitalità dei cibi vivi e naturali",
                "Alleggerire il corpo e la mente, migliorando anche l'umore e la creatività",
                "Prepararti all'estate in modo sano e armonioso, senza forzature"
              ].map((item, index) => (
                <li key={index} className="flex items-start">
                  <div className="mr-3 pt-1 text-green">
                    <Check size={20} />
                  </div>
                  <p className="body text-text-dark">{item}</p>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="text-center">
            <h3 className="h3 text-green mb-4">
              <span className="text-accent">📘</span> Cosa troverai nel corso?
            </h3>
            <p className="body text-text-dark">
              Ricette semplici e gustose, consigli pratici, rituali di purificazione quotidiana.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const LifestyleSection = () => {
  return (
    <section className="bg-green py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8">
            <h2 className="h2 text-beige-light mb-4">
              <span className="text-beige-light">🌿</span> Non è una dieta. È uno stile di vita.
            </h2>
            <p className="body text-text-light">
              Depurarsi non significa privarsi, ma rimettere ordine e armonia tra corpo, emozioni e mente. 
              Ritrovare il contatto con la ciclicità della natura, ridurre ciò che è in eccesso, 
              e nutrirsi con piacere e consapevolezza.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhatYouWillLearnSection = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  
  const learningPoints = [
    {
      id: 1,
      title: "Come riconoscere i disagi del corpo come segnali preziosi",
      description: "Adiposità, stanchezza cronica, cisti, gonfiore e altri segnali del corpo"
    },
    {
      id: 2,
      title: "Come ascoltare i tuoi bisogni fondamentali",
      description: "E superare le resistenze al cambiamento"
    },
    {
      id: 3,
      title: "Il legame tra alimentazione ripetitiva e disturbi cronici",
      description: "Scopri come variare il tuo modo di nutrirti"
    },
    {
      id: 4,
      title: "Come muovere l'energia con il cibo",
      description: "Secondo l'orologio biologico e i principi della medicina tradizionale"
    },
    {
      id: 5,
      title: "A mangiare meno e meglio",
      description: "Riscoprendo il gusto dei cibi naturali"
    },
    {
      id: 6,
      title: "A vivere la depurazione come un'esperienza rigenerante",
      description: "Non come una punizione, ma come un dono per il tuo corpo"
    }
  ];

  return (
    <section className="bg-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="h2 text-green text-center mb-12">
            <span className="text-accent">💡</span> Cosa imparerai
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {learningPoints.map((point) => (
              <div
                key={point.id}
                className="p-6 bg-beige-light rounded-15 shadow-md transition-all duration-300"
                onMouseEnter={() => setHoveredCard(point.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  transform: hoveredCard === point.id ? 'translateY(-8px)' : 'translateY(0)',
                  boxShadow: hoveredCard === point.id ? '0 10px 25px rgba(0, 0, 0, 0.1)' : ''
                }}
              >
                <div className="flex items-start mb-3">
                  <div className="mr-3 pt-1 text-green">
                    <Check size={20} />
                  </div>
                  <h3 className="h4 text-green">{point.title}</h3>
                </div>
                <p className="body-tile text-text-dark ml-8">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// New accordion component for program content
const Accordion = ({ title, children, icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-4 bg-white rounded-15 shadow-md overflow-hidden">
      <button
        className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors duration-200"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <span className="text-accent mr-3">{icon}</span>
          <h3 className="h3 text-green">{title}</h3>
        </div>
        <div className="text-green">
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <div 
        className="overflow-hidden transition-all duration-300"
        style={{ 
          maxHeight: isOpen ? '500px' : '0',
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden'
        }}
      >
        <div className="p-6 border-t border-gray-100">
          {children}
        </div>
      </div>
    </div>
  );
};

const ProgramSection = () => {
  const programSections = [
    {
      id: 1,
      title: "Consapevolezza & corpo",
      icon: "🔎",
      items: [
        "Alimentazione come stile di vita (non come rinuncia)",
        "Il disagio fisico ed emotivo come messaggio",
        "Superare ostacoli e abitudini dannose",
        "Ascoltare il corpo e respirare consapevolmente"
      ]
    },
    {
      id: 2,
      title: "Alimentazione e vitalità",
      icon: "🥬",
      items: [
        "Il cibo secondo le stagioni (legge dei 5 movimenti)",
        "Cibi vitali ricchi di Jing",
        "Cibi drenanti e antinfiammatori",
        "Cibi rinfrescanti/riscaldanti, rilassanti/contraenti",
        "Quando mangiare e perché mangiare meno",
        "Come depurare in base al momento e al tuo stato"
      ]
    },
    {
      id: 3,
      title: "Il digiuno (fisico e mentale)",
      icon: "🧘‍♀️",
      items: [
        "Digiuno intermittente",
        "Digiuno occasionale",
        "Mima digiuno (ispirato al metodo Valter Longo)",
        "Digiuno per la mente: silenzio, contemplazione, mindfulness"
      ]
    }
  ];

  const recipes = [
    "Verdure pressate",
    "Tè Kukicha",
    "Zuppa di miso",
    "Brodo di clorofilla",
    "Brodo base detox",
    "Pane di Oshawa",
    "Frutta cotta con kuzu",
    "Tofu stufato"
  ];

  return (
    <section className="bg-beige-light py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="h2 text-green text-center mb-12">
            <span className="text-accent">📗</span> Il programma completo (DISPENSA)
          </h2>
          
          <div className="space-y-4 mb-12">
            {programSections.map((section) => (
              <Accordion key={section.id} title={section.title} icon={section.icon}>
                <ul className="space-y-2">
                  {section.items.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <div className="mr-3 pt-1 text-green">
                        <span className="text-xl">•</span>
                      </div>
                      <p className="body text-text-dark">{item}</p>
                    </li>
                  ))}
                </ul>
              </Accordion>
            ))}
          </div>
          
          <div className="bg-green p-6 rounded-15 shadow-md text-beige-light">
            <h3 className="h3 mb-6 text-center">
              <span className="text-beige-light mr-2">🍲</span>
              E-BOOK + RICETTE PRATICHE + VIDEORICETTE
            </h3>
            <p className="body mb-4">
              Impara direttamente dalla Dott.ssa Filzoli come preparare piatti semplici, gustosi e detox:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {recipes.map((recipe, index) => (
                <div key={index} className="flex items-start">
                  <div className="mr-3 pt-1">
                    <Check size={16} />
                  </div>
                  <p className="body-tile">{recipe}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Completely redesigned checkout section
const CheckoutSection = () => {
  const productId = "18bdfc62-c891-4690-8745-ee6d01392336"; // Replace with your actual product ID
  
  // Testimonials - add social proof
  const testimonials = [
    {
      name: "Laura T.",
      text: "Ho seguito questo corso e dopo solo 2 settimane ho notato una differenza incredibile nella mia energia e nella mia pelle!",
      avatar: "https://api.placeholder.com/40/50"
    },
    {
      name: "Marco S.",
      text: "Le ricette sono deliziose e sorprendentemente semplici. La mia famiglia non ha nemmeno notato che stavamo mangiando 'detox'!",
      avatar: "https://api.placeholder.com/40/50"
    }
  ];


    return (
      <section id="acquista" className="py-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-full h-16 bg-gradient-to-b from-beige-light to-transparent"></div>
        
        {/* Highlight effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-green/20 to-accent/15 pointer-events-none"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Top banner */}
            <div className="bg-gradient-to-r from-green to-accent py-4 px-6 text-center text-white">
              <p className="font-bold text-lg">🔥 OFFERTA SPECIALE A TEMPO LIMITATO 🔥</p>
            </div>
            
            <div className="p-8 md:p-12">
              {/* Title and pricing - more prominent */}
              <div className="text-center mb-12">
                <h2 className="h1 text-green mb-6">
                  Inizia il tuo percorso detox
                </h2>
                
                {/* Pricing section with animated glow effect */}
                <div className="mb-8">
                  {/* Original price and savings */}
                  <div className="flex justify-center items-center mb-4">
                    <div className="text-gray-400 line-through text-2xl">89€</div>
                    <div className="mx-4 text-gray-500 text-xl">-</div>
                    <div className="text-pink-500 font-bold text-2xl">40€</div>
                    <div className="ml-3 bg-gradient-to-r from-pink-500 to-accent text-white px-4 py-1 rounded-full text-sm font-bold">RISPARMI</div>
                  </div>
                  
                  {/* Final price with animated glow */}
                  <div 
                    className="relative mx-auto bg-gradient-to-r from-green to-accent text-white py-6 px-12 rounded-2xl shadow-lg inline-block mb-2"
                    style={{ 
                      animation: "pulseShadow 2s infinite alternate",
                    }}
                  >
                    <style jsx>{`
                      @keyframes pulseShadow {
                        0% {
                          box-shadow: 0 0 10px rgba(74, 222, 128, 0.4), 0 0 20px rgba(74, 222, 128, 0.2);
                        }
                        100% {
                          box-shadow: 0 0 20px rgba(74, 222, 128, 0.6), 0 0 40px rgba(74, 222, 128, 0.3);
                        }
                      }
                    `}</style>
                    <span className="text-sm uppercase tracking-widest block mb-1">Prezzo finale</span>
                    <div className="text-5xl font-bold">49€</div>
                  </div>
                  
                  {/* One-time payment note */}
                  <p className="text-gray-600 italic mt-2">Pagamento unico, accesso a vita</p>
                </div>
              </div>
              
              {/* Course includes - visual highlight */}
              <div className="mb-12 bg-beige-light p-6 rounded-xl">
                <h3 className="h3 text-green mb-6 text-center">
                  Il corso include:
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                    <div className="mr-4 bg-green text-white p-2 rounded-full">
                      <Check size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-green">E-Book Completo</h4>
                      <p className="text-gray-600">Con spiegazioni dettagliate della Dott.ssa Filzoli</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                    <div className="mr-4 bg-green text-white p-2 rounded-full">
                      <Check size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-green">Videoricette accessibili online</h4>
                      <p className="text-gray-600">Segui il libro in maniera pratica</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                    <div className="mr-4 bg-green text-white p-2 rounded-full">
                      <Check size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-green">Accesso permanente</h4>
                      <p className="text-gray-600">Al corso e a tutti i futuri aggiornamenti</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                    <div className="mr-4 bg-green text-white p-2 rounded-full">
                      <Check size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-green">Supporto dedicato</h4>
                      <p className="text-gray-600">Per domande e chiarimenti durante il percorso</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Purchase form with enhanced visual */}
              <div className="bg-gradient-to-r from-green/25 to-accent/20 p-6 rounded-xl shadow-md mb-8">
                {/*<h3 className="h3 text-green mb-6 text-center">
                  Acquista ora e inizia subito
                </h3>*/}
                
                <PurchaseFormContent productId={productId} />
                
                {/* Trust badges */}
                <div className="mt-6 flex flex-wrap justify-center gap-4">
                  <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-sm">
                    <Calendar className="text-green mr-2" size={20} />
                    <p className="text-sm text-gray-600">Accesso immediato</p>
                  </div>
                  <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-sm">
                    <Shield className="text-green mr-2" size={20} />
                    <p className="text-sm text-gray-600">Pagamento sicuro</p>
                  </div>
                  <div className="flex items-center bg-white px-4 py-2 rounded-lg shadow-sm">
                    <Clock className="text-green mr-2" size={20} />
                    <p className="text-sm text-gray-600">Supporto 24/7</p>
                  </div>
                </div>
              </div>
              
              {/* Guarantee */}
              <div className="bg-gradient-to-r from-green/20 to-accent/15 p-6 rounded-xl text-center mb-8">
                <h3 className="font-bold text-green mb-2">Garanzia di soddisfazione</h3>
                <p className="text-gray-600">Se non sei soddisfatto entro 14 giorni dall'acquisto, riceverai un rimborso completo, senza domande.</p>
              </div>
              
              {/* Final CTA 
              <div className="text-center">
                <ScrollToSectionButton 
                  targetId="acquista"
                  className="inline-flex items-center justify-center px-8 py-4 bg-green text-white font-bold text-lg rounded-full hover:bg-green-dark transition-colors duration-300 shadow-lg transform hover:scale-105 transition-transform"
                >
                  <ShoppingCart className="mr-2" size={20} />
                  Acquista ora e inizia il tuo percorso detox
                </ScrollToSectionButton>
                <p className="mt-4 text-sm text-gray-500">L'offerta speciale è valida solo per un periodo limitato!</p>
              </div>*/}
            </div>
          </div>
        </div>
      </section>
    );
  };

const Page = () => {
  return (
    <div className="flex flex-col">
      <HeroSection />
      <IntroSection />
      <AboutDoctorSection />
      <WhyDetoxSection />
      <LifestyleSection />
      <WhatYouWillLearnSection />
      <ProgramSection />
      <CheckoutSection />
    </div>
  );
};

export default Page;